<template>
  <v-card class="mb-4" flat outlined>
    <v-card-title class="subtitle-1 font-weight-bold">Logos</v-card-title>
    <v-card-text>
      <v-row dense>
        <!-- Logo de tela -->
        <v-col cols="12" md="6">
          <div class="logo-bloco pa-3">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2">mdi-image</v-icon>
              <span class="font-weight-medium">Logo de tela</span>
            </div>
            <p class="text-caption text-medium-emphasis mb-3">
              Usada no site e no aplicativo. PNG, JPG ou WebP, até 2 MB.
            </p>

            <div class="preview mb-3">
              <img v-if="logo.url" :src="logo.url" alt="Logo do cliente" />
              <span v-else class="text-caption text-medium-emphasis">
                Nenhuma logo cadastrada
              </span>
            </div>

            <v-file-input
              v-model="logo.arquivo"
              label="Selecionar imagem"
              accept=".png,.jpg,.jpeg,.webp"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              density="compact"
              variant="outlined"
              show-size
              :error-messages="logo.erro"
              @update:model-value="selecionarLogo"
            />

            <v-btn
              color="primary"
              variant="tonal"
              block
              :loading="logo.enviando"
              :disabled="!arquivoDe(logo.arquivo) || !!logo.erro"
              @click="enviarLogo"
            >
              <v-icon start>mdi-upload</v-icon>
              Enviar logo
            </v-btn>
          </div>
        </v-col>

        <!-- Logo térmica -->
        <v-col cols="12" md="6">
          <div class="logo-bloco pa-3">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2">mdi-printer-pos</v-icon>
              <span class="font-weight-medium">Logo para impressão térmica</span>
            </div>
            <p class="text-caption text-medium-emphasis mb-3">
              Arte já preparada para a bobina: BMP 1-bit (preto e branco) na
              largura escolhida. PNG e JPG também são aceitos. A API guarda o
              arquivo como enviado, sem converter.
            </p>

            <div class="preview mb-3">
              <img
                v-if="termica.url"
                :src="termica.url"
                alt="Logo térmica do cliente"
              />
              <span v-else class="text-caption text-medium-emphasis">
                Nenhuma logo térmica cadastrada
              </span>
            </div>

            <v-select
              v-model="termica.largura"
              :items="largurasTermicas"
              item-title="rotulo"
              item-value="valor"
              label="Largura da bobina"
              density="compact"
              variant="outlined"
            />

            <v-file-input
              v-model="termica.arquivo"
              label="Selecionar imagem"
              accept=".bmp,.png,.jpg,.jpeg,.webp"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              density="compact"
              variant="outlined"
              show-size
              :error-messages="termica.erro"
              @update:model-value="selecionarTermica"
            />

            <v-alert
              v-if="termica.aviso"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-3 text-caption"
            >
              {{ termica.aviso }}
            </v-alert>

            <v-btn
              color="primary"
              variant="tonal"
              block
              :loading="termica.enviando"
              :disabled="!arquivoDe(termica.arquivo) || !!termica.erro"
              @click="enviarTermica"
            >
              <v-icon start>mdi-upload</v-icon>
              Enviar logo térmica
            </v-btn>

            <p v-if="termica.largura_atual" class="text-caption mt-2 mb-0">
              Cadastrada para {{ termica.largura_atual }} px
              ({{ rotuloLargura(termica.largura_atual) }})
            </p>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { apiV1 } from "@/services/api";
import { mapActions } from "vuex";

const TAMANHO_MAXIMO = 2 * 1024 * 1024;
const MIMES_TELA = ["image/png", "image/jpeg", "image/webp"];
const MIMES_TERMICA = [...MIMES_TELA, "image/bmp", "image/x-ms-bmp"];
// O app redimensiona para a largura da bobina e limita a altura em 248 px;
// acima disso a arte sai achatada na impressão.
const ALTURA_MAXIMA_TERMICA = 248;

export default {
  name: "LogoCliente",
  props: {
    codcliente: { type: [Number, String], required: true },
  },
  data: () => ({
    logo: { url: null, arquivo: null, erro: "", enviando: false },
    termica: {
      url: null,
      arquivo: null,
      erro: "",
      aviso: "",
      enviando: false,
      largura: 384,
      largura_atual: null,
    },
    largurasTermicas: [
      { valor: 384, rotulo: "58 mm — 384 px (32 colunas)" },
      { valor: 576, rotulo: "80 mm — 576 px (48 colunas)" },
    ],
  }),
  mounted() {
    this.carregarLogos();
  },
  methods: {
    ...mapActions(["triggerFeedback"]),

    // O v-file-input devolve File ou File[] conforme a versão do Vuetify.
    arquivoDe(valor) {
      if (!valor) return null;
      return Array.isArray(valor) ? valor[0] || null : valor;
    },

    rotuloLargura(valor) {
      const item = this.largurasTermicas.find((l) => l.valor === valor);
      return item ? item.rotulo.split(" — ")[0] : "personalizada";
    },

    async carregarLogos() {
      try {
        const { data } = await apiV1.get(`/clientes/${this.codcliente}/logo`);
        this.aplicarResposta(data);
      } catch (error) {
        // Cliente sem logo não é erro: a rota responde com os campos nulos.
        console.error(error);
      }
    },

    aplicarResposta(data) {
      this.logo.url = data.logo_url;
      this.termica.url = data.logo_termica_url;
      this.termica.largura_atual = data.logo_termica_largura;
      if (data.logo_termica_largura) {
        this.termica.largura = data.logo_termica_largura;
      }
    },

    validar(arquivo, mimesAceitos) {
      if (!arquivo) return "";
      if (!mimesAceitos.includes(arquivo.type)) {
        return "Formato não suportado.";
      }
      if (arquivo.size > TAMANHO_MAXIMO) {
        return "Imagem excede 2 MB.";
      }
      return "";
    },

    selecionarLogo(valor) {
      this.logo.erro = this.validar(this.arquivoDe(valor), MIMES_TELA);
    },

    selecionarTermica(valor) {
      const arquivo = this.arquivoDe(valor);
      this.termica.erro = this.validar(arquivo, MIMES_TERMICA);
      this.termica.aviso = "";
      if (arquivo && !this.termica.erro) {
        this.avaliarAlturaTermica(arquivo);
      }
    },

    // Aviso, não bloqueio: a arte funciona, só sai achatada.
    avaliarAlturaTermica(arquivo) {
      const url = URL.createObjectURL(arquivo);
      const imagem = new Image();
      imagem.onload = () => {
        const alturaFinal = Math.round(
          (imagem.height * this.termica.largura) / imagem.width
        );
        if (alturaFinal > ALTURA_MAXIMA_TERMICA) {
          this.termica.aviso =
            `Em ${this.termica.largura} px de largura esta arte fica com ` +
            `${alturaFinal} px de altura. O app corta em ${ALTURA_MAXIMA_TERMICA} px, ` +
            `então ela sairá achatada. Reduza a altura da arte.`;
        }
        URL.revokeObjectURL(url);
      };
      imagem.onerror = () => URL.revokeObjectURL(url);
      imagem.src = url;
    },

    async enviarLogo() {
      const arquivo = this.arquivoDe(this.logo.arquivo);
      if (!arquivo) return;

      this.logo.enviando = true;
      try {
        const corpo = new FormData();
        corpo.append("arquivo", arquivo);
        const { data } = await apiV1.put(
          `/clientes/${this.codcliente}/logo`,
          corpo
        );
        this.aplicarResposta(data);
        this.logo.arquivo = null;
        this.triggerFeedback({
          message: "Logo enviada com sucesso!",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (error) {
        this.avisarErro(error, "Erro ao enviar a logo!");
      }
      this.logo.enviando = false;
    },

    async enviarTermica() {
      const arquivo = this.arquivoDe(this.termica.arquivo);
      if (!arquivo) return;

      this.termica.enviando = true;
      try {
        const corpo = new FormData();
        corpo.append("arquivo", arquivo);
        corpo.append("largura", this.termica.largura);
        const { data } = await apiV1.put(
          `/clientes/${this.codcliente}/logo-termica`,
          corpo
        );
        this.aplicarResposta(data);
        this.termica.arquivo = null;
        this.termica.aviso = "";
        this.triggerFeedback({
          message: "Logo térmica enviada com sucesso!",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (error) {
        this.avisarErro(error, "Erro ao enviar a logo térmica!");
      }
      this.termica.enviando = false;
    },

    avisarErro(error, padrao) {
      console.error(error);
      this.triggerFeedback({
        message: error?.response?.data?.detail || padrao,
        color: "error",
        icon: "mdi-alert-circle",
      });
    },
  },
};
</script>

<style scoped>
.logo-bloco {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  height: 100%;
}
.preview {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 8px;
  border-radius: 4px;
  /* Xadrez para enxergar transparência e arte branca. */
  background-image: linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
    linear-gradient(-45deg, transparent 75%, #e0e0e0 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0px;
  background-color: #fafafa;
}
.preview img {
  max-width: 100%;
  max-height: 160px;
  object-fit: contain;
}
</style>
